import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Layout from "./components/Layout";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import VerifyOTP from "./pages/VerifyOTP";
import ForgotPassword from "./pages/ForgotPassword";
import AuthCallback from "./pages/AuthCallback";
import Profile from "./pages/Profile";
import EditProfile from "./pages/EditProfile";
import Notifications from "./pages/Notifications";
import PublicProfessionalProfile from "./pages/PublicProfessionalProfile";
import PublicFacilityProfile from "./pages/PublicFacilityProfile";

// Patient Pages
import PatientDashboard from "./pages/patient/Dashboard";
import SearchProfessionals from "./pages/patient/SearchProfessionals";
import ProfessionalDetail from "./pages/patient/ProfessionalDetail";
import Appointments from "./pages/patient/Appointments";
import HomeCare from "./pages/patient/HomeCare";
import MedicalRecords from "./pages/patient/MedicalRecords";
import MedicalRecordsUpload from "./pages/patient/MedicalRecordsUpload";
import MedicalReminders from "./pages/patient/MedicalReminders";
import PatientConsultations from "./pages/patient/Consultations";
import FamilyMembers from "./pages/patient/FamilyMembers";
import PatientCredits from "./pages/patient/Credits";

// Professional Pages
import ProfessionalDashboard from "./pages/professional/Dashboard";
import ProfessionalSetup from "./pages/professional/Setup";
import ProfessionalProfile from "./pages/professional/Profile";
import HomeCareRequests from "./pages/professional/HomeCareRequests";
import ProfessionalAppointments from "./pages/professional/Appointments";
import ProfessionalCredits from "./pages/professional/Credits";

// Admin Pages
import AdminDashboard from "./pages/admin/Dashboard";
import AdminValidations from "./pages/admin/Validations";
import AdminUsers from "./pages/admin/Users";
import AdminStats from "./pages/admin/Stats";
import AdminMissions from "./pages/admin/Missions";

// Agent Pages
import AgentDashboard from "./pages/agent/Dashboard";

// Facility Pages
import FacilityDashboard from "./pages/facility/Dashboard";
import FacilityProfile from "./pages/facility/Profile";
import FacilityProfessionals from "./pages/facility/Professionals";
import FacilityProducts from "./pages/facility/Products";
import FacilityQueue from "./pages/facility/Queue";
import FacilityEvents from "./pages/facility/Events";
import FacilityVerification from "./pages/facility/Verification";
import FacilityCredits from "./pages/facility/Credits";

// KYC Verification
import KYCVerification from "./pages/KYCVerification";

// Products Catalog
import ProductsSearch from "./pages/ProductsSearch";

// Queue System
import QueueTicket from "./pages/QueueTicket";
import QueuePublic from "./pages/QueuePublic";

// Facilities Pages
import Facilities from "./pages/Facilities";
import FacilityDetail from "./pages/FacilityDetail";

// Pharmacy Pages
import { PharmacySearch, PharmacyDetail, PatientPrescriptions } from "./pages/Pharmacies";
import PharmacyDashboard from "./pages/pharmacy/Dashboard";

// Video Room
import VideoRoom from "./pages/VideoRoom";

// Chat
import ChatRoom, { ConversationsList } from "./pages/Chat";

// AI Assistant
import AIAssistant from "./pages/AIAssistant";

// Credits
import Credits from "./pages/Credits";
import AdminCredits from "./pages/admin/Credits";

// AI Chat Bubble for guests
import AIChatBubble from "./components/AIChatBubble";

// PWA Install Prompt
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import PWAUpdateNotification from "./components/PWAUpdateNotification";

// Legal & Info Pages
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import RefundPolicy from "./pages/RefundPolicy";
import Contact from "./pages/Contact";
import FAQ from "./pages/FAQ";
import About from "./pages/About";
import MentionsLegales from "./pages/MentionsLegales";
import JoinProfessionals from "./pages/JoinProfessionals";
import JoinAgents from "./pages/JoinAgents";

// Shared Medical Profile (public)
import SharedMedicalProfile from "./pages/SharedMedicalProfile";

// Directory Pages (public)
import ProfessionalsDirectory from "./pages/ProfessionalsDirectory";
import FacilitiesDirectory from "./pages/FacilitiesDirectory";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();

  // If returning from OAuth callback, let AuthCallback handle it
  if (location.state?.user) {
    return children;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    // Redirect to appropriate dashboard based on role
    switch (user?.role) {
      case 'patient':
        return <Navigate to="/dashboard" replace />;
      case 'professional':
        return <Navigate to="/pro/dashboard" replace />;
      case 'admin':
        return <Navigate to="/admin/dashboard" replace />;
      case 'agent':
        return <Navigate to="/agent/dashboard" replace />;
      case 'pharmacy':
        return <Navigate to="/pharmacy/dashboard" replace />;
      case 'facility':
        return <Navigate to="/facility/dashboard" replace />;
      default:
        return <Navigate to="/login" replace />;
    }
  }

  return children;
};

// Public Route - redirect if already logged in
const PublicRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    switch (user?.role) {
      case 'patient':
        return <Navigate to="/dashboard" replace />;
      case 'professional':
        return <Navigate to="/pro/dashboard" replace />;
      case 'admin':
        return <Navigate to="/admin/dashboard" replace />;
      case 'agent':
        return <Navigate to="/agent/dashboard" replace />;
      case 'pharmacy':
        return <Navigate to="/pharmacy/dashboard" replace />;
      case 'facility':
        return <Navigate to="/facility/dashboard" replace />;
      default:
        return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
};

// Dashboard Router - routes to appropriate dashboard based on role
const DashboardRouter = () => {
  const { user } = useAuth();

  switch (user?.role) {
    case 'professional':
      return <ProfessionalDashboard />;
    case 'admin':
      return <AdminDashboard />;
    case 'agent':
      return <AgentDashboard />;
    case 'pharmacy':
      return <PharmacyDashboard />;
    case 'facility':
      return <FacilityDashboard />;
    default:
      return <PatientDashboard />;
  }
};

// App Router that handles OAuth callback detection
const AppRouter = () => {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment for session_id synchronously during render
  // This prevents race conditions by processing new session_id FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={
        <PublicRoute>
          <Landing />
        </PublicRoute>
      } />
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <Register />
        </PublicRoute>
      } />
      <Route path="/verify-otp" element={
        <VerifyOTP />
      } />
      <Route path="/forgot-password" element={
        <PublicRoute>
          <ForgotPassword />
        </PublicRoute>
      } />
      
      {/* Public Professional Profile */}
      <Route path="/dr/:professionalId" element={
        <PublicProfessionalProfile />
      } />
      
      {/* SEO-friendly Public Routes */}
      <Route path="/pro/:slug" element={<PublicProfessionalProfile />} />
      <Route path="/etablissement/:slug" element={<PublicFacilityProfile />} />

      {/* Legal & Info Pages (Public) */}
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/about" element={<About />} />
      <Route path="/mentions-legales" element={<MentionsLegales />} />
      <Route path="/rejoindre" element={<JoinProfessionals />} />
      <Route path="/devenir-agent" element={<JoinAgents />} />

      {/* Protected Routes - All roles */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Layout>
            <DashboardRouter />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Layout>
            <Profile />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/edit-profile" element={
        <ProtectedRoute>
          <Layout>
            <EditProfile />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/notifications" element={
        <ProtectedRoute>
          <Layout>
            <Notifications />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/credits" element={
        <ProtectedRoute>
          <Layout>
            <Credits />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/kyc" element={
        <ProtectedRoute allowedRoles={['professional', 'facility', 'agent']}>
          <Layout>
            <KYCVerification />
          </Layout>
        </ProtectedRoute>
      } />

      {/* Patient Routes */}
      <Route path="/search" element={
        <ProtectedRoute allowedRoles={['patient', 'agent']}>
          <Layout>
            <SearchProfessionals />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/professionals/:id" element={
        <ProtectedRoute allowedRoles={['patient', 'agent']}>
          <Layout>
            <ProfessionalDetail />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/appointments" element={
        <ProtectedRoute allowedRoles={['patient']}>
          <Layout>
            <Appointments />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/home-care" element={
        <ProtectedRoute allowedRoles={['patient']}>
          <Layout>
            <HomeCare />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/facilities" element={
        <Layout>
          <Facilities />
        </Layout>
      } />
      <Route path="/facilities/:id" element={
        <Layout>
          <FacilityDetail />
        </Layout>
      } />
      <Route path="/medical-records" element={
        <ProtectedRoute allowedRoles={['patient']}>
          <Layout>
            <MedicalRecords />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/patient/medical-records" element={
        <ProtectedRoute allowedRoles={['patient']}>
          <Layout>
            <MedicalRecords />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/patient/medical-records/upload" element={
        <ProtectedRoute allowedRoles={['patient']}>
          <Layout>
            <MedicalRecordsUpload />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/patient/consultations" element={
        <ProtectedRoute allowedRoles={['patient']}>
          <Layout>
            <PatientConsultations />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/patient/family" element={
        <ProtectedRoute allowedRoles={['patient']}>
          <Layout>
            <FamilyMembers />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/patient/prescriptions" element={
        <ProtectedRoute allowedRoles={['patient']}>
          <Layout>
            <PatientPrescriptions />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/patient/reminders" element={
        <ProtectedRoute allowedRoles={['patient']}>
          <Layout>
            <MedicalReminders />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/patient/credits" element={
        <ProtectedRoute allowedRoles={['patient']}>
          <Layout>
            <PatientCredits />
          </Layout>
        </ProtectedRoute>
      } />

      {/* Pharmacy Public Routes */}
      <Route path="/pharmacies" element={
        <Layout>
          <PharmacySearch />
        </Layout>
      } />
      <Route path="/pharmacies/:pharmacyId" element={
        <Layout>
          <PharmacyDetail />
        </Layout>
      } />

      {/* Pharmacy Dashboard Routes */}
      <Route path="/pharmacy/dashboard" element={
        <ProtectedRoute allowedRoles={['pharmacy']}>
          <Layout>
            <PharmacyDashboard />
          </Layout>
        </ProtectedRoute>
      } />

      {/* Facility Routes */}
      <Route path="/facility/dashboard" element={
        <ProtectedRoute allowedRoles={['facility']}>
          <Layout>
            <FacilityDashboard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/facility/profile" element={
        <ProtectedRoute allowedRoles={['facility']}>
          <Layout>
            <FacilityProfile />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/facility/professionals" element={
        <ProtectedRoute allowedRoles={['facility']}>
          <Layout>
            <FacilityProfessionals />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/facility/products" element={
        <ProtectedRoute allowedRoles={['facility']}>
          <Layout>
            <FacilityProducts />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/facility/queue" element={
        <ProtectedRoute allowedRoles={['facility']}>
          <Layout>
            <FacilityQueue />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/facility/events" element={
        <ProtectedRoute allowedRoles={['facility']}>
          <Layout>
            <FacilityEvents />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/facility/verification" element={
        <ProtectedRoute allowedRoles={['facility']}>
          <Layout>
            <FacilityVerification />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/facility/credits" element={
        <ProtectedRoute allowedRoles={['facility']}>
          <Layout>
            <FacilityCredits />
          </Layout>
        </ProtectedRoute>
      } />

      {/* Products Catalog (Public) */}
      <Route path="/catalogue" element={
        <Layout>
          <ProductsSearch />
        </Layout>
      } />

      {/* Queue System (Public) */}
      <Route path="/queue/:queueId/ticket" element={<QueueTicket />} />
      <Route path="/queue/:queueId" element={<QueuePublic />} />
      <Route path="/queue/ticket/:ticketId" element={<QueueTicket />} />

      {/* Shared Medical Profile (Public) */}
      <Route path="/shared/medical/:token" element={<SharedMedicalProfile />} />

      {/* Public Directories */}
      <Route path="/professionnels" element={<ProfessionalsDirectory />} />
      <Route path="/etablissements" element={<FacilitiesDirectory />} />

      {/* Professional Routes */}
      <Route path="/pro/dashboard" element={
        <ProtectedRoute allowedRoles={['professional']}>
          <Layout>
            <ProfessionalDashboard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/pro/setup" element={
        <ProtectedRoute allowedRoles={['professional']}>
          <Layout>
            <ProfessionalSetup />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/pro/profile" element={
        <ProtectedRoute allowedRoles={['professional']}>
          <Layout>
            <ProfessionalProfile />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/pro/appointments" element={
        <ProtectedRoute allowedRoles={['professional']}>
          <Layout>
            <ProfessionalAppointments />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/pro/credits" element={
        <ProtectedRoute allowedRoles={['professional']}>
          <Layout>
            <ProfessionalCredits />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/pro/home-care" element={
        <ProtectedRoute allowedRoles={['professional']}>
          <Layout>
            <HomeCareRequests />
          </Layout>
        </ProtectedRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin/dashboard" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminDashboard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/validations" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminValidations />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminUsers />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/credits" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminCredits />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/stats" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminStats />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/missions" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminMissions />
          </Layout>
        </ProtectedRoute>
      } />

      {/* Agent Routes */}
      <Route path="/agent/dashboard" element={
        <ProtectedRoute allowedRoles={['agent']}>
          <Layout>
            <AgentDashboard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/agent/patients" element={
        <ProtectedRoute allowedRoles={['agent']}>
          <Layout>
            <AgentDashboard />
          </Layout>
        </ProtectedRoute>
      } />

      {/* Video Room Route */}
      <Route path="/video-room/:appointmentId" element={
        <ProtectedRoute allowedRoles={['patient', 'professional']}>
          <VideoRoom />
        </ProtectedRoute>
      } />

      {/* Chat Routes */}
      <Route path="/chat" element={
        <ProtectedRoute allowedRoles={['patient', 'professional']}>
          <Layout>
            <ConversationsList />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/chat/:conversationId" element={
        <ProtectedRoute allowedRoles={['patient', 'professional']}>
          <ChatRoom />
        </ProtectedRoute>
      } />

      {/* AI Assistant Route */}
      <Route path="/ai-assistant" element={
        <ProtectedRoute allowedRoles={['patient', 'professional']}>
          <Layout>
            <AIAssistant />
          </Layout>
        </ProtectedRoute>
      } />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Guest AI Bubble - Only shows when not authenticated
const GuestAIBubble = () => {
  const { isAuthenticated, loading } = useAuth();
  
  // Don't show if loading or authenticated
  if (loading || isAuthenticated) return null;
  
  return <AIChatBubble />;
};

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
            <AppRouter />
            <GuestAIBubble />
          </BrowserRouter>
          <Toaster position="top-center" richColors />
          <PWAInstallPrompt />
          <PWAUpdateNotification />
        </div>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
